import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MobileLayout from '../components/MobileLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Edit, Trash2, Bell, X, Clock, Users, User as UserIcon, Check, Info, ChevronDown, ChevronUp, RefreshCw, Heart, Volume2 } from 'lucide-react';
import { PointsBadge, PointsCelebration } from '../components/PointsDisplay';

// Alarm sound utility - plays a sound at event time
const playAlarmSound = () => {
  try {
    // Create an audio context for the alarm sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for alarm tone
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Alarm sound pattern - alternating tones
    oscillator.frequency.value = 880; // A5 note
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    
    // Pattern: beep-beep-beep
    let beepCount = 0;
    const beepInterval = setInterval(() => {
      if (beepCount >= 6) {
        clearInterval(beepInterval);
        oscillator.stop();
        audioContext.close();
        return;
      }
      gainNode.gain.value = beepCount % 2 === 0 ? 0.3 : 0;
      beepCount++;
    }, 300);
    
    // Stop after 2 seconds
    setTimeout(() => {
      clearInterval(beepInterval);
      oscillator.stop();
      audioContext.close();
    }, 2000);
    
  } catch (e) {
    console.log('Could not play alarm sound:', e);
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const placeholderTexts = {
  "My Birthday": "Get celebrated by your partner",
  "Our Anniversary": "Never forget this special day",
  "Date Night": "1x per month minimum",
  "My Mother's Birthday": "Important dates matter",
  "My Father's Birthday": "Important dates matter",
  "My Siblings' Birthday": "Family matters",
  "Weekly Relationship Check In": "5 min weekly sync",
  "Social Activities": "Plan fun times together",
  "Personal Events": "Your important moments"
};

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Helper to calculate reminder time
const getMillisUntilReminder = (event) => {
  if (!event.reminder_enabled || !event.reminder_before || !event.event_date) return null;
  
  const eventDate = new Date(event.event_date);
  if (event.event_time) {
    const [hours, minutes] = event.event_time.split(':');
    eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  } else {
    eventDate.setHours(8, 0, 0, 0); // Default to 8 AM
  }
  
  let reminderDate = new Date(eventDate);
  
  switch (event.reminder_before) {
    case '15_min':
      reminderDate.setMinutes(reminderDate.getMinutes() - 15);
      break;
    case '30_min':
      reminderDate.setMinutes(reminderDate.getMinutes() - 30);
      break;
    case '1_hour':
      reminderDate.setHours(reminderDate.getHours() - 1);
      break;
    case '2_hours':
      reminderDate.setHours(reminderDate.getHours() - 2);
      break;
    case '1_day':
      reminderDate.setDate(reminderDate.getDate() - 1);
      break;
    case '2_days':
      reminderDate.setDate(reminderDate.getDate() - 2);
      break;
    case '1_week':
      reminderDate.setDate(reminderDate.getDate() - 7);
      break;
    case '2_weeks':
      reminderDate.setDate(reminderDate.getDate() - 14);
      break;
    default:
      reminderDate.setDate(reminderDate.getDate() - 1);
  }
  
  const now = new Date();
  const msUntil = reminderDate.getTime() - now.getTime();
  
  return msUntil > 0 ? msUntil : null;
};

// Helper to calculate time until alarm (at event time)
const getMillisUntilAlarm = (event) => {
  if (!event.alarm_enabled || !event.event_date) return null;
  
  const eventDate = new Date(event.event_date);
  if (event.event_time) {
    const [hours, minutes] = event.event_time.split(':');
    eventDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  } else {
    eventDate.setHours(8, 0, 0, 0); // Default to 8 AM
  }
  
  const now = new Date();
  const msUntil = eventDate.getTime() - now.getTime();
  
  return msUntil > 0 ? msUntil : null;
};

const RemindersContent = ({ user }) => {
  // Load cached data instantly to avoid flash (except suggestions - backend is source of truth)
  const [events, setEvents] = useState(() => {
    const cached = localStorage.getItem('cached_events');
    return cached ? JSON.parse(cached) : [];
  });
  const [placeholders, setPlaceholders] = useState([]);
  const [dismissedPlaceholders, setDismissedPlaceholders] = useState(() => {
    const saved = localStorage.getItem('dismissedPlaceholders');
    return saved ? JSON.parse(saved) : [];
  });
  // Suggestions permanently hidden - ONLY from backend (resets on disconnect/profile delete)
  const [suggestionsPermanentlyHidden, setSuggestionsPermanentlyHidden] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false); // Track if prefs loaded from backend
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState(() => {
    const cached = localStorage.getItem('cached_connectionInfo');
    return cached ? JSON.parse(cached) : null;
  });
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [conflictWarning, setConflictWarning] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isPastEventsCollapsed, setIsPastEventsCollapsed] = useState(true);
  const navigate = useNavigate();
  
  // Track scheduled alarms to avoid duplicates
  const scheduledAlarmsRef = useRef(new Set());
  
  // Points state - load from cache
  const [userPoints, setUserPoints] = useState(() => {
    const cached = localStorage.getItem('cached_userPoints');
    return cached ? parseInt(cached, 10) : 0;
  });
  const [showPointsCelebration, setShowPointsCelebration] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [pointsMessage, setPointsMessage] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    frequency: 'once',
    custom_days: [],
    target: 'both',
    alarm_enabled: false,
    reminder_enabled: false,
    reminder_before: '1_day'
  });
  
  // Handle deep link from notification - open specific event
  // Using ref to avoid setState warning in effect
  const eventIdFromUrl = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event');
    if (eventId) {
      // Clear the URL parameter
      window.history.replaceState({}, '', '/reminders');
    }
    return eventId;
  }, []);
  
  // When events load and we have a notification event ID, open it
  useEffect(() => {
    if (eventIdFromUrl && events.length > 0) {
      const event = events.find(e => e.event_id === eventIdFromUrl);
      if (event) {
        // Use setTimeout to avoid setState in effect warning
        setTimeout(() => setViewingEvent(event), 0);
      }
    }
  }, [eventIdFromUrl, events]);
  
  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return;
    }
    
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    return permission;
  }, []);
  
  // Schedule notification for an event
  const scheduleNotification = useCallback((event) => {
    const msUntil = getMillisUntilReminder(event);
    if (!msUntil || msUntil > 24 * 60 * 60 * 1000) return; // Only schedule within 24 hours
    
    // Store scheduled notification ID to avoid duplicates
    const notifKey = `notif_${event.event_id}`;
    const existingNotif = localStorage.getItem(notifKey);
    if (existingNotif) return;
    
    const timeoutId = setTimeout(() => {
      if (Notification.permission === 'granted') {
        const notification = new Notification(`CoupleIn: ${event.title}`, {
          body: event.description || `Reminder: ${event.title} is coming up!`,
          icon: '/logo192.png',
          tag: event.event_id,
          requireInteraction: true
        });
        
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
        
        // Also show toast in-app
        toast.info(`🔔 Reminder: ${event.title}`, {
          duration: 10000,
          description: event.description || 'Your event is coming up!'
        });
      }
      localStorage.removeItem(notifKey);
    }, msUntil);
    
    localStorage.setItem(notifKey, String(timeoutId));
  }, []);
  
  // Schedule alarm sound for an event (at event time)
  const scheduleAlarm = useCallback((event) => {
    const msUntil = getMillisUntilAlarm(event);
    if (!msUntil || msUntil > 24 * 60 * 60 * 1000) return; // Only schedule within 24 hours
    
    // Avoid duplicates
    if (scheduledAlarmsRef.current.has(event.event_id)) return;
    scheduledAlarmsRef.current.add(event.event_id);
    
    setTimeout(() => {
      // Play alarm sound
      playAlarmSound();
      
      // Show notification with sound
      if (Notification.permission === 'granted') {
        const notification = new Notification(`🔔 ALARM: ${event.title}`, {
          body: `It's time! ${event.event_time || '8:00 AM'}`,
          icon: '/logo192.png',
          tag: `alarm_${event.event_id}`,
          requireInteraction: true
        });
        
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
      
      // Show in-app alert
      toast.success(`🔔 ALARM: ${event.title}`, {
        duration: 15000,
        description: `It's time! ${event.event_time || '8:00 AM'}`
      });
      
      // Remove from scheduled
      scheduledAlarmsRef.current.delete(event.event_id);
    }, msUntil);
  }, []);
  
  // Setup notifications for all events with reminders
  const setupNotifications = useCallback(async () => {
    if (!('Notification' in window)) return;
    
    if (Notification.permission === 'default') {
      await requestNotificationPermission();
    }
    
    setNotificationPermission(Notification.permission);
    
    if (Notification.permission === 'granted') {
      events.forEach(event => {
        if (event.reminder_enabled && !event.completed) {
          scheduleNotification(event);
        }
        // Schedule alarms (sound at event time)
        if (event.alarm_enabled && !event.completed) {
          scheduleAlarm(event);
        }
      });
    }
  }, [events, requestNotificationPermission, scheduleNotification, scheduleAlarm]);
  
  const fetchData = async () => {
    try {
      const [eventsRes, placeholdersRes, statusRes, prefsRes] = await Promise.all([
        axios.get(`${API}/events`, { withCredentials: true }),
        axios.get(`${API}/placeholders`, { withCredentials: true }),
        axios.get(`${API}/browny/status`, { withCredentials: true }).catch(() => ({ data: { user_points: 0 } })),
        axios.get(`${API}/user/preferences`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      
      // Sort events by proximity (closest first)
      const sortedEvents = eventsRes.data.sort((a, b) => {
        const dateA = new Date(a.event_date);
        const dateB = new Date(b.event_date);
        return dateA - dateB;
      });
      
      // Update state and cache for instant load next time
      setEvents(sortedEvents);
      localStorage.setItem('cached_events', JSON.stringify(sortedEvents));
      
      setPlaceholders(placeholdersRes.data.placeholders);
      
      const points = statusRes.data?.user_points || 0;
      setUserPoints(points);
      localStorage.setItem('cached_userPoints', String(points));
      
      // Check if suggestions are permanently hidden FROM BACKEND (source of truth)
      // Backend resets this on disconnect/profile delete
      const suggestionsHidden = prefsRes.data?.calendar_suggestions_hidden === true;
      setSuggestionsPermanentlyHidden(suggestionsHidden);
      setPrefsLoaded(true);
      
      // If backend says suggestions should show, also clear local dismissed list
      // This handles the case where user disconnected and should see all suggestions fresh
      if (!suggestionsHidden) {
        // Check if we need to reset dismissed placeholders (new relationship)
        const lastPartnerId = localStorage.getItem('last_partner_id');
        const currentPartnerId = statusRes.data?.partner_id || null;
        
        if (lastPartnerId && lastPartnerId !== currentPartnerId) {
          // Partner changed - reset dismissed suggestions
          setDismissedPlaceholders([]);
          localStorage.removeItem('dismissedPlaceholders');
        }
        
        // Store current partner ID for next comparison
        if (currentPartnerId) {
          localStorage.setItem('last_partner_id', currentPartnerId);
        } else {
          localStorage.removeItem('last_partner_id');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load events');
    }
  };
  
  const fetchConnectionInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/connection-info`, {
        withCredentials: true
      });
      setConnectionInfo(response.data);
      localStorage.setItem('cached_connectionInfo', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching connection info:', error);
    }
  };
  
  useEffect(() => {
    fetchData();
    fetchConnectionInfo();
  }, []);
  
  // Setup notifications when events change
  useEffect(() => {
    if (events.length > 0) {
      setupNotifications();
    }
  }, [events, setupNotifications]);
  
  const checkForConflicts = (date) => {
    if (!date) return null;
    const conflicting = events.filter(e => 
      e.event_date === date && 
      (!editingEvent || e.event_id !== editingEvent.event_id)
    );
    return conflicting.length > 0 ? conflicting : null;
  };
  
  const handleDateChange = (date) => {
    setFormData({...formData, event_date: date});
    const conflicts = checkForConflicts(date);
    if (conflicts) {
      setConflictWarning(conflicts);
    } else {
      setConflictWarning(null);
    }
  };
  
  const openDialog = (placeholder = null, event = null) => {
    setConflictWarning(null);
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        description: event.description || '',
        event_date: event.event_date,
        event_time: event.event_time || '',
        frequency: event.frequency,
        custom_days: event.custom_days || [],
        target: event.target,
        alarm_enabled: event.alarm_enabled,
        reminder_enabled: event.reminder_enabled || false,
        reminder_before: event.reminder_before || '1_day'
      });
    } else if (placeholder) {
      // Use placeholder text directly as title
      const suggestedFrequency = placeholder.includes('Birthday') || placeholder.includes('Anniversary') ? 'yearly' : 
                                  placeholder.includes('Weekly') ? 'weekly' : 'once';
      
      setFormData({
        title: placeholder,
        description: '',
        event_date: '',
        event_time: '',
        frequency: suggestedFrequency,
        custom_days: [],
        target: 'both',
        alarm_enabled: false,
        reminder_enabled: false,
        reminder_before: '1_day'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        frequency: 'once',
        custom_days: [],
        target: 'both',
        alarm_enabled: false,
        reminder_enabled: false,
        reminder_before: '1_day'
      });
    }
    setIsDialogOpen(true);
  };
  
  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingEvent(null);
    setConflictWarning(null);
    setFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '',
      frequency: 'once',
      custom_days: [],
      target: 'both',
      alarm_enabled: false,
      reminder_enabled: false,
      reminder_before: '1_day'
    });
  };
  
  const toggleCustomDay = (day) => {
    setFormData(prev => ({
      ...prev,
      custom_days: prev.custom_days.includes(day)
        ? prev.custom_days.filter(d => d !== day)
        : [...prev.custom_days, day]
    }));
  };
  
  const handleSubmit = async () => {
    if (!formData.title || !formData.event_date) {
      toast.error('Title and date are required');
      return;
    }
    
    // Only allow future dates when CREATING new events (not when editing)
    if (!editingEvent) {
      const selectedDate = new Date(formData.event_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        toast.error('Please select a future date for new events');
        return;
      }
    }
    
    if (formData.frequency === 'custom_days' && formData.custom_days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    
    try {
      const prevPoints = userPoints;
      
      if (editingEvent) {
        await axios.put(
          `${API}/events/${editingEvent.event_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Event updated!');
      } else {
        await axios.post(
          `${API}/events`,
          formData,
          { withCredentials: true }
        );
        
        // Show points celebration for new event
        const pointsAwarded = formData.target === 'self' ? 25 : 50;
        setPointsEarned(pointsAwarded);
        setPointsMessage(formData.target === 'self' ? 'Personal event created!' : 'Shared event created! 💕');
        setShowPointsCelebration(true);
      }
      
      closeDialog();
      await fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save event';
      toast.error(errorMsg);
      console.error('Save error:', error);
    }
  };
  
  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted');
      setDeleteConfirmId(null);
      setViewingEvent(null);
      await fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 403) {
        toast.error('You can only delete events you created');
      } else if (status === 404) {
        toast.error('Event not found - it may have already been deleted');
        setViewingEvent(null);
        await fetchData();
      } else {
        toast.error(detail || 'Failed to delete event');
      }
      setDeleteConfirmId(null);
    }
  };
  
  const handleMarkComplete = async (event, closeDialog = true) => {
    try {
      // Mark current event as completed
      await axios.put(
        `${API}/events/${event.event_id}`,
        { ...event, completed: true, completed_at: new Date().toISOString() },
        { withCredentials: true }
      );
      
      // If recurring, create next occurrence
      if (event.frequency !== 'once') {
        const currentDate = new Date(event.event_date);
        let nextDate = new Date(currentDate);
        
        switch (event.frequency) {
          case 'daily':
            nextDate.setDate(nextDate.getDate() + 1);
            break;
          case 'weekly':
            nextDate.setDate(nextDate.getDate() + 7);
            break;
          case 'biweekly':
            nextDate.setDate(nextDate.getDate() + 14);
            break;
          case 'monthly':
            nextDate.setMonth(nextDate.getMonth() + 1);
            break;
          case 'yearly':
            nextDate.setFullYear(nextDate.getFullYear() + 1);
            break;
          case 'weekdays':
            // Move to next weekday
            do {
              nextDate.setDate(nextDate.getDate() + 1);
            } while (nextDate.getDay() === 0 || nextDate.getDay() === 6);
            break;
          case 'custom_days':
            // Find next matching day
            if (event.custom_days && event.custom_days.length > 0) {
              const dayMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };
              const targetDays = event.custom_days.map(d => dayMap[d]).sort((a, b) => a - b);
              let found = false;
              for (let i = 1; i <= 7 && !found; i++) {
                nextDate.setDate(nextDate.getDate() + 1);
                if (targetDays.includes(nextDate.getDay())) {
                  found = true;
                }
              }
            }
            break;
          default:
            break;
        }
        
        // Create next event
        const nextEventData = {
          title: event.title,
          description: event.description,
          event_date: nextDate.toISOString().split('T')[0],
          event_time: event.event_time,
          frequency: event.frequency,
          custom_days: event.custom_days,
          target: event.target,
          alarm_enabled: event.alarm_enabled,
          reminder_enabled: event.reminder_enabled,
          reminder_before: event.reminder_before,
          completed: false
        };
        
        await axios.post(`${API}/events`, nextEventData, { withCredentials: true });
        toast.success(`✓ Completed! Next ${event.frequency} event created.`);
      } else {
        toast.success('✓ Event marked as completed!');
      }
      
      // Show points celebration for completing event
      const pointsAwarded = event.target === 'self' ? 25 : 25; // Both get 25 initially, bonus comes if partner also completes
      setPointsEarned(pointsAwarded);
      setPointsMessage('Event completed! Great job! ✅');
      setShowPointsCelebration(true);
      
      if (closeDialog) {
        setViewingEvent(null);
      }
      await fetchData();
    } catch (error) {
      toast.error('Failed to mark as complete');
      console.error('Complete error:', error);
    }
  };
  
  const dismissPlaceholder = (placeholder, e) => {
    e.stopPropagation();
    const updated = [...dismissedPlaceholders, placeholder];
    setDismissedPlaceholders(updated);
    localStorage.setItem('dismissedPlaceholders', JSON.stringify(updated));
    toast.success('Suggestion hidden');
  };
  
  // Permanently hide suggestions (persisted to backend - resets on disconnect/profile delete)
  const hideSuggestionsPermanently = async () => {
    try {
      await axios.put(`${API}/user/preferences`, {
        calendar_suggestions_hidden: true
      }, { withCredentials: true });
      setSuggestionsPermanentlyHidden(true);
      // Also clear dismissed placeholders since they're all hidden now
      setDismissedPlaceholders([]);
      localStorage.removeItem('dismissedPlaceholders');
      toast.success('Suggestions completed');
    } catch (error) {
      console.error('Failed to save preference:', error);
      toast.error('Failed to save preference');
    }
  };
  
  const resetDismissedPlaceholders = () => {
    setDismissedPlaceholders([]);
    localStorage.removeItem('dismissedPlaceholders');
    toast.success('All suggestions restored');
  };
  
  const visiblePlaceholders = placeholders.filter(p => !dismissedPlaceholders.includes(p));
  
  const getTargetIcon = (target) => {
    if (target === 'both') return <Users className="w-3.5 h-3.5" />;
    return <UserIcon className="w-3.5 h-3.5" />;
  };
  
  const getFrequencyDisplay = (event) => {
    if (event.frequency === 'custom_days' && event.custom_days) {
      return event.custom_days.map(d => d.substring(0, 3)).join(', ');
    }
    return event.frequency.charAt(0).toUpperCase() + event.frequency.slice(1);
  };
  
  const getTimeTogether = () => {
    if (!user?.relationship_start_date) return null;
    const start = new Date(user.relationship_start_date);
    const now = new Date();
    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    let days = now.getDate() - start.getDate();
    if (days < 0) {
      months--;
      const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    if (days > 0) parts.push(`${days}d`);
    return parts.join(' ') || '0d';
  };
  
  const getDaysUntil = (dateStr) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const diff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff < 0) return `${Math.abs(diff)}d ago`;
    if (diff < 7) return `${diff}d`;
    if (diff < 30) return `${Math.floor(diff / 7)}w`;
    return `${Math.floor(diff / 30)}mo`;
  };
  
  const isPastEvent = (dateStr) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  };
  
  // Format date for event card (e.g., "16 December")
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  };
  
  // Format time for event card (e.g., "11:59 PM")
  const formatEventTime = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  
  // Check if any event has reminders enabled
  const hasRemindersEnabled = events.some(e => e.reminder_enabled && !e.completed);
  
  // Separate completed and upcoming events
  const upcomingEvents = events.filter(e => !e.completed && !isPastEvent(e.event_date));
  const pastEvents = events.filter(e => e.completed || isPastEvent(e.event_date));
  
  return (
    <MobileLayout>
      {/* Points Celebration Popup */}
      <PointsCelebration
        show={showPointsCelebration}
        points={pointsEarned}
        message={pointsMessage}
        onClose={() => setShowPointsCelebration(false)}
        currentTotal={userPoints}
      />
      
      <div className="min-h-full bg-gray-50" data-testid="reminders-page">
        {/* ============================================
            HEADER SECTION - Gradient Banner (matches Connect with Partner)
            ============================================ */}
        <div 
          className="relative px-5 pt-5 pb-12"
          style={{
            background: 'linear-gradient(135deg, #FF6B9D 0%, #FF8A80 50%, #FFB74D 100%)',
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
          }}
        >
          {/* Top Row: Title + Points + Avatars */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-white drop-shadow-sm">
                Never forget. Ever!
              </h1>
              
              {/* Relationship Duration Pill */}
              {connectionInfo?.has_partner && user?.relationship_start_date && (
                <div className="mt-2.5 inline-flex items-center gap-1.5 bg-white/90 rounded-full px-3 py-1.5 shadow-sm">
                  <Heart className="w-4 h-4 text-pink-500" fill="currentColor" />
                  <span className="text-sm font-medium text-gray-800">
                    Together {getTimeTogether()}
                  </span>
                </div>
              )}
              
              {/* Connect partner prompt */}
              {!connectionInfo?.has_partner && (
                <button
                  onClick={() => navigate('/connection')}
                  className="mt-2.5 inline-flex items-center gap-1.5 bg-white/90 rounded-full px-3 py-1.5 shadow-sm tap-feedback"
                >
                  <Users className="w-4 h-4 text-pink-500" />
                  <span className="text-sm font-medium text-gray-800">
                    Connect Partner
                  </span>
                </button>
              )}
            </div>
            
            {/* Right side: Points + Avatars */}
            <div className="flex flex-col items-end gap-2">
              <PointsBadge points={userPoints} />
              
              {/* Profile Pictures - Overlapping */}
              <div className="flex -space-x-2.5">
                <img 
                  src={user?.picture || 'https://via.placeholder.com/44'} 
                  alt=""
                  className="w-11 h-11 rounded-full border-2 border-white object-cover shadow-sm"
                />
                {connectionInfo?.has_partner && connectionInfo.partner && (
                  <img 
                    src={connectionInfo.partner.picture || 'https://via.placeholder.com/44'} 
                    alt=""
                    className="w-11 h-11 rounded-full border-2 border-white object-cover shadow-sm"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* ============================================
            MAIN CONTENT - White Card Overlay
            ============================================ */}
        <div 
          className="relative bg-white rounded-t-3xl -mt-6 min-h-[calc(100vh-180px)] pb-24"
          style={{ boxShadow: '0 -4px 20px rgba(0,0,0,0.05)' }}
        >
          <div className="px-5 pt-5">
            
            {/* Section Header: Upcoming Events */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Upcoming Events</h2>
              <button 
                onClick={() => openDialog()}
                data-testid="create-event-btn"
                className="tap-icon p-1"
              >
                <Plus className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Single Suggestion Card - Only show if not permanently hidden AND prefs loaded */}
            {prefsLoaded && !suggestionsPermanentlyHidden && visiblePlaceholders.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Suggestion {placeholders.length - visiblePlaceholders.length + 1} of {placeholders.length}
                  </p>
                  {dismissedPlaceholders.length > 0 && (
                    <button 
                      onClick={resetDismissedPlaceholders}
                      className="text-xs text-primary font-medium tap-feedback"
                    >
                      Reset
                    </button>
                  )}
                </div>
                
                <div
                  className="relative bg-gradient-to-r from-primary/5 to-orange-50 rounded-2xl px-4 py-4 tap-list-item border border-primary/10 animate-suggest"
                >
                  <button
                    onClick={(e) => dismissPlaceholder(visiblePlaceholders[0], e)}
                    className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-200 tap-icon"
                    aria-label="Skip suggestion"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                  <button
                    onClick={() => openDialog(visiblePlaceholders[0])}
                    className="text-left w-full pr-10"
                  >
                    <p className="text-base font-semibold text-gray-900">{visiblePlaceholders[0]}</p>
                    <p className="text-sm text-gray-500 mt-1">{placeholderTexts[visiblePlaceholders[0]]}</p>
                  </button>
                </div>
              </div>
            )}
            
            {/* Completed suggestions message - two options: Start Over or Close Permanently */}
            {prefsLoaded && !suggestionsPermanentlyHidden && visiblePlaceholders.length === 0 && dismissedPlaceholders.length > 0 && (
              <div className="bg-green-50 rounded-2xl p-4 border border-green-100 mb-4">
                <div className="text-center">
                  <p className="text-sm text-green-700 font-medium mb-3">🎉 All suggestions reviewed!</p>
                  <div className="flex gap-2 justify-center">
                    <button 
                      onClick={resetDismissedPlaceholders}
                      className="px-4 py-2 bg-white border border-green-200 text-green-700 text-sm font-medium rounded-xl tap-feedback hover:bg-green-50"
                    >
                      Start Over
                    </button>
                    <button 
                      onClick={hideSuggestionsPermanently}
                      className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-xl tap-feedback hover:bg-green-700"
                    >
                      Close Permanently
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* ============================================
                EVENTS LIST
                ============================================ */}
            <div className="space-y-3">
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                  <p className="text-gray-400">No upcoming events</p>
                  <button
                    onClick={() => openDialog()}
                    className="mt-3 text-primary font-medium text-sm tap-feedback"
                  >
                    + Add your first event
                  </button>
                </div>
              ) : (
                upcomingEvents.map((event) => (
                  <button
                    key={event.event_id}
                    data-testid={`event-${event.event_id}`}
                    onClick={() => setViewingEvent(event)}
                    className="w-full bg-white rounded-2xl border border-gray-100 shadow-soft p-4 text-left tap-card"
                  >
                    {/* Event Card - New Layout */}
                    <div className="flex items-start justify-between gap-3">
                      {/* Left: Time + Title + Description */}
                      <div className="flex-1 min-w-0">
                        {/* Time Row */}
                        <div className="flex items-center gap-1.5 text-gray-500 mb-1.5">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {event.event_time ? formatEventTime(event.event_time) : '8:00 AM'}
                          </span>
                          {event.alarm_enabled && (
                            <Volume2 className="w-3.5 h-3.5 text-orange-500 ml-1" />
                          )}
                          {event.frequency && event.frequency !== 'once' && (
                            <span className="ml-1 px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] font-medium rounded-full flex items-center gap-0.5">
                              <RefreshCw className="w-2.5 h-2.5" />
                              {event.frequency === 'custom_days' ? 'Custom' : event.frequency.charAt(0).toUpperCase() + event.frequency.slice(1)}
                            </span>
                          )}
                        </div>
                        
                        {/* Title */}
                        <h3 className="font-semibold text-gray-900 truncate mb-0.5">
                          {event.title}
                        </h3>
                        
                        {/* Description (subtitle) - if exists */}
                        {event.description && (
                          <p className="text-sm text-gray-500 line-clamp-2">
                            {event.description}
                          </p>
                        )}
                        
                        {/* Target + Creator */}
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            {getTargetIcon(event.target)}
                            {event.target === 'both' ? 'Both' : event.target === 'partner' ? 'Partner' : 'You'}
                          </span>
                          {event.created_by_name && (
                            <span className="flex items-center gap-1">
                              <UserIcon className="w-3 h-3" />
                              {event.created_by_name.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Right: Date */}
                      <div className="flex flex-col items-end text-right flex-shrink-0">
                        <CalendarIcon className="w-4 h-4 text-gray-400 mb-1" />
                        <span className="text-sm font-medium text-gray-700">
                          {formatEventDate(event.event_date)}
                        </span>
                        <span className={`text-xs mt-0.5 font-medium ${getDaysUntil(event.event_date) === 'Today' ? 'text-primary' : 'text-gray-400'}`}>
                          {getDaysUntil(event.event_date)}
                        </span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            
            {/* ============================================
                PAST/COMPLETED EVENTS - Collapsible
                ============================================ */}
            {pastEvents.length > 0 && (
              <div className="mt-6 space-y-3">
                <button 
                  onClick={() => setIsPastEventsCollapsed(!isPastEventsCollapsed)}
                  className="flex items-center justify-between w-full tap-feedback"
                >
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Past & Completed ({pastEvents.length})
                  </p>
                  {isPastEventsCollapsed ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                
                {!isPastEventsCollapsed && (
                  <div className="space-y-2">
                    {pastEvents.map((event) => (
                      <div
                        key={event.event_id}
                        className="w-full bg-gray-50 rounded-2xl p-4 text-left opacity-70"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            {event.completed ? (
                              <Check className="w-5 h-5 text-green-500" />
                            ) : (
                              <span className="text-sm font-bold text-gray-400">{new Date(event.event_date).getDate()}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-600 truncate">{event.title}</h3>
                              {event.frequency && event.frequency !== 'once' && (
                                <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-[10px] font-medium rounded-full flex items-center gap-0.5">
                                  <RefreshCw className="w-2.5 h-2.5" />
                                  {event.frequency === 'custom_days' ? 'Custom' : event.frequency.charAt(0).toUpperCase() + event.frequency.slice(1)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400">{getDaysUntil(event.event_date)}</p>
                          </div>
                          {/* Mark complete button for past but not completed events */}
                          {!event.completed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkComplete(event, false);
                              }}
                              className="px-3 py-1.5 bg-green-50 text-green-600 text-xs font-medium rounded-lg tap-button flex items-center gap-1"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Complete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Collapsed preview */}
                {isPastEventsCollapsed && (
                  <p className="text-xs text-gray-400 text-center py-2">
                    Tap to show {pastEvents.length} past event{pastEvents.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* ============================================
            EVENT DETAIL VIEW DIALOG
            ============================================ */}
        <Dialog open={!!viewingEvent} onOpenChange={() => setViewingEvent(null)}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl">{viewingEvent?.title}</DialogTitle>
            </DialogHeader>
            
            {viewingEvent && (
              <div className="space-y-4 py-2">
                {/* Created by */}
                {viewingEvent.created_by_name && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <UserIcon className="w-4 h-4" />
                    <span>Created by {viewingEvent.created_by_name}</span>
                  </div>
                )}
                
                {/* Date & Time */}
                <div className="flex items-center gap-3 text-gray-600">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium">
                      {new Date(viewingEvent.event_date).toLocaleDateString('en-GB', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                    {viewingEvent.event_time && (
                      <p className="text-sm text-gray-400">{formatEventTime(viewingEvent.event_time)}</p>
                    )}
                  </div>
                </div>
                
                {/* Description */}
                {viewingEvent.description && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700">{viewingEvent.description}</p>
                  </div>
                )}
                
                {/* Details */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs mb-1">Repeat</p>
                    <p className="font-medium text-gray-700">{getFrequencyDisplay(viewingEvent)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs mb-1">For</p>
                    <p className="font-medium text-gray-700">
                      {viewingEvent.target === 'both' ? 'Both of us' : viewingEvent.target === 'partner' ? 'Partner' : 'Just me'}
                    </p>
                  </div>
                </div>
                
                {/* Alarm status */}
                {viewingEvent.alarm_enabled && (
                  <div className="flex items-center gap-2 text-orange-600 bg-orange-50 rounded-xl p-3">
                    <Bell className="w-4 h-4" fill="currentColor" />
                    <span className="text-sm font-medium">Alarm enabled</span>
                  </div>
                )}
                
                {/* Status badge */}
                <div className={`text-center py-2 rounded-xl text-sm font-medium ${
                  viewingEvent.completed 
                    ? 'bg-green-50 text-green-600' 
                    : isPastEvent(viewingEvent.event_date)
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-primary/10 text-primary'
                }`}>
                  {viewingEvent.completed 
                    ? '✓ Completed' 
                    : isPastEvent(viewingEvent.event_date)
                      ? 'Past event'
                      : getDaysUntil(viewingEvent.event_date)}
                </div>
              </div>
            )}
            
            <DialogFooter className="flex flex-col gap-2">
              {/* Mark as Completed button - show for both upcoming and past events that aren't completed */}
              {viewingEvent && !viewingEvent.completed && (
                <Button
                  onClick={() => handleMarkComplete(viewingEvent)}
                  className="w-full h-12 rounded-xl bg-green-500 hover:bg-green-600"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark as Completed
                </Button>
              )}
              
              <div className="flex gap-2 w-full">
                {/* Show Edit button only for own events or partner's "both" events */}
                {(viewingEvent?.user_id === user?.user_id || 
                  (viewingEvent?.user_id !== user?.user_id && viewingEvent?.target === 'both')) && (
                  <Button
                    onClick={() => {
                      setViewingEvent(null);
                      openDialog(null, viewingEvent);
                    }}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
                
                {/* Only show delete button if user created this event */}
                {viewingEvent?.user_id === user?.user_id ? (
                  deleteConfirmId === viewingEvent?.event_id ? (
                    <div className="flex gap-2 flex-1">
                      <Button
                        onClick={() => handleDelete(viewingEvent.event_id)}
                        className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600"
                      >
                        Delete
                      </Button>
                      <Button
                        onClick={() => setDeleteConfirmId(null)}
                        variant="outline"
                        className="h-11 rounded-xl"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setDeleteConfirmId(viewingEvent?.event_id)}
                      variant="outline"
                      className="flex-1 h-11 rounded-xl text-red-500 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )
                ) : (
                  <div className="flex-1 text-center text-xs text-gray-400 py-3">
                    Created by {viewingEvent?.created_by_name?.split(' ')[0] || 'partner'}
                  </div>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* ============================================
            CREATE/EDIT EVENT DIALOG
            ============================================ */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent className="rounded-2xl max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm font-medium">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Event name"
                  className="mt-1.5 h-11 rounded-xl"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Add details..."
                  className="mt-1.5 rounded-xl min-h-[80px]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Date</Label>
                  <Input
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="mt-1.5 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Time</Label>
                  <Input
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => setFormData({...formData, event_time: e.target.value})}
                    className="mt-1.5 h-11 rounded-xl"
                  />
                </div>
              </div>
              
              {/* Conflict Info - Light heads up */}
              {conflictWarning && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-700">Just a heads up!</p>
                      <p className="text-xs text-blue-500 mt-1">
                        You already have {conflictWarning.length} event{conflictWarning.length > 1 ? 's' : ''} on this day:
                      </p>
                      <ul className="text-xs text-blue-600 mt-1">
                        {conflictWarning.map(e => (
                          <li key={e.event_id}>• {e.title}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium">Repeat</Label>
                <Select value={formData.frequency} onValueChange={(val) => setFormData({...formData, frequency: val, custom_days: []})}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="weekdays">Weekdays</SelectItem>
                    <SelectItem value="custom_days">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.frequency === 'custom_days' && (
                <div className="grid grid-cols-2 gap-2">
                  {weekdays.map((day) => (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={day}
                        checked={formData.custom_days.includes(day)}
                        onCheckedChange={() => toggleCustomDay(day)}
                      />
                      <label htmlFor={day} className="text-sm">{day}</label>
                    </div>
                  ))}
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium">For</Label>
                <Select value={formData.target} onValueChange={(val) => setFormData({...formData, target: val})}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both of us</SelectItem>
                    <SelectItem value="partner">Partner only</SelectItem>
                    <SelectItem value="self">Just me</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-4 h-4 text-orange-500" />
                    <Label className="text-sm font-medium">Sound alarm at event time</Label>
                  </div>
                  <Switch
                    checked={formData.alarm_enabled}
                    onCheckedChange={(checked) => setFormData({...formData, alarm_enabled: checked})}
                  />
                </div>
                {formData.alarm_enabled && (
                  <p className="text-xs text-gray-400 ml-6">
                    Plays an alarm sound at {formData.event_time ? formatEventTime(formData.event_time) : '8:00 AM'}
                  </p>
                )}
              </div>
              
              <div className="flex items-center justify-between py-2">
                <Label className="text-sm font-medium">Remind me before</Label>
                <Switch
                  checked={formData.reminder_enabled}
                  onCheckedChange={(checked) => setFormData({...formData, reminder_enabled: checked})}
                />
              </div>
              
              {formData.reminder_enabled && (
                <div>
                  <Label className="text-sm font-medium">How early?</Label>
                  <Select 
                    value={formData.reminder_before} 
                    onValueChange={(val) => setFormData({...formData, reminder_before: val})}
                  >
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15_min">15 minutes before</SelectItem>
                      <SelectItem value="30_min">30 minutes before</SelectItem>
                      <SelectItem value="1_hour">1 hour before</SelectItem>
                      <SelectItem value="2_hours">2 hours before</SelectItem>
                      <SelectItem value="1_day">1 day before</SelectItem>
                      <SelectItem value="2_days">2 days before</SelectItem>
                      <SelectItem value="1_week">1 week before</SelectItem>
                      <SelectItem value="2_weeks">2 weeks before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button
                onClick={handleSubmit}
                className="w-full h-12 rounded-xl gradient-brand shadow-pink font-semibold text-white"
              >
                {editingEvent ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MobileLayout>
  );
};

const Reminders = () => {
  return (
    <ProtectedRoute>
      <RemindersContent />
    </ProtectedRoute>
  );
};

export default Reminders;
